import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../../components/SparkleEffect';
import { GlitterHeart } from '../../components/GlitterHeart';
import { CalendarIcon, RepeatIcon, RefreshCwIcon, SparklesIcon, SmileIcon, MessageCircleHeartIcon, ZapIcon, CheckSquareIcon, DumbbellIcon, HomeIcon, PaletteIcon, ShieldCheckIcon, ArrowRightIcon, ClockIcon } from 'lucide-react';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.base69e03f5b54ed4865741c1bde.app';
export function TrippinThroughLifePage() {
  const features = [{
    icon: <CalendarIcon className="w-6 h-6" data-id="element-1194" data-mp="1mb7r9yex531p" />,
    title: 'AI-powered personalized schedules'
  }, {
    icon: <RepeatIcon className="w-6 h-6" data-id="element-1195" data-mp="2xo0ecvwrl3bf" />,
    title: 'Adaptive routines and recurring chore systems'
  }, {
    icon: <RefreshCwIcon className="w-6 h-6" data-id="element-1196" data-mp="32mldst46ziyt" />,
    title: 'Flexible and rotating schedule support'
  }, {
    icon: <SparklesIcon className="w-6 h-6" data-id="element-1197" data-mp="1t7t1n9wbbsz8" />,
    title: 'Deep cleaning checklists and home reset plans'
  }, {
    icon: <SmileIcon className="w-6 h-6" data-id="element-1198" data-mp="1p80oejvz4ynj" />,
    title: 'Mood check-ins and wellness support'
  }, {
    icon: <MessageCircleHeartIcon className="w-6 h-6" data-id="element-1199" data-mp="1ka8wvqz8rejv" />,
    title: 'Daily affirmations and motivation'
  }, {
    icon: <ZapIcon className="w-6 h-6" data-id="element-1200" data-mp="1a9yk27h4c958" />,
    title: 'Personalized "Tiny Badass Actions"'
  }, {
    icon: <CheckSquareIcon className="w-6 h-6" data-id="element-1201" data-mp="15ji5ul5h4jht" />,
    title: 'AI-generated custom checklists'
  }, {
    icon: <DumbbellIcon className="w-6 h-6" data-id="element-1202" data-mp="11h4y5r762bie" />,
    title: 'Fitness and self-care routine tracking'
  }, {
    icon: <HomeIcon className="w-6 h-6" data-id="element-1203" data-mp="wjmpm1x29yax" />,
    title: 'Family and household task organization'
  }, {
    icon: <PaletteIcon className="w-6 h-6" data-id="element-1204" data-mp="riz0jlse7rjc" />,
    title: 'Customizable themes and personalized app experience'
  }];
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-peach-50 pt-28 pb-24" data-id="element-1205" data-mp="mmqfjrril449">
      <SparkleEffect count={80} data-id="element-1206" data-mp="cz4m9dozb37f" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1207" data-mp="hw4u9u5e2g16">
        {/* Hero Section */}
        <div className="text-center mb-16" data-id="element-1208" data-mp="3wvayllvz5sz">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="w-32 h-32 md:w-48 md:h-48 mx-auto mb-8 rounded-3xl bg-white shadow-xl shadow-blush-200/50 flex items-center justify-center p-4 border border-blush-100" data-id="element-1209" data-mp="3y6j0xmd6zpw">
            <img src="https://cdn.magicpatterns.com/uploads/gieTJfs2YUH6aZyKV1oyEk/ChatGPT_Image_May_11,_2026,_01_53_32_PM.png" alt="Trippin' Through Life App Logo" className="w-full h-full object-contain" data-id="element-1210" data-mp="mupjkyj3rfau" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="font-heading text-4xl md:text-6xl font-bold text-plum-800 mb-6" data-id="element-1211" data-mp="1jdne60l3o2vj">
            Trippin' Through Life
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-plum-600 font-medium max-w-3xl mx-auto leading-relaxed" data-id="element-1212" data-mp="38urd5s9ctw0k">
            Your AI-powered life, home, and wellness management system designed
            to reduce overwhelm, build routines, and help you stay on top of
            everyday life — without burnout.
          </motion.p>
        </div>

        {/* Description */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-blush-100 mb-16" data-id="element-1213" data-mp="5flp83iwh9fu">
          <p className="text-plum-700 text-lg leading-relaxed mb-6" data-id="element-1214" data-mp="2p2dm0mg44m0l">
            Using intelligent conversational onboarding, TTL learns your unique
            lifestyle, household structure, schedules, routines, stress levels,
            cleaning needs, and personal goals to create fully personalized
            daily plans, chore systems, reset routines, wellness check-ins, and
            smart checklists tailored specifically to you.
          </p>
          <p className="text-plum-700 text-lg leading-relaxed font-medium" data-id="element-1215" data-mp="37tmzt7wx1wxz">
            Whether you're managing a busy family, flexible work schedule,
            content creation, school routines, mental overload, fitness goals,
            or simply trying to get your life back on track, TTL adapts to your
            real life — not the other way around.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="mb-20" data-id="element-1216" data-mp="3hv7du1vfg582">
          <h2 className="font-heading text-3xl font-bold text-center text-plum-800 mb-10" data-id="element-1217" data-mp="3hwijk78fnn1t">
            Everything you need to thrive
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1218" data-mp="ef0icin61ead">
            {features.map((feature, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05
          }} className="flex items-center gap-4 bg-white/60 backdrop-blur-sm p-5 rounded-2xl border border-blush-200/50 shadow-sm hover:bg-white transition-colors" data-id="element-1219" data-mp="9ffb7kxudzmk" data-mpi={idx}>
                <div className="w-12 h-12 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500 shrink-0" data-id="element-1220" data-mp="2rm9q9r7ah4qc">
                  {feature.icon}
                </div>
                <span className="font-bold text-plum-700" data-id="element-1221" data-mp="2hvx2lq46gvfz">{feature.title}</span>
              </motion.div>)}
          </div>
        </div>

        {/* Tagline & Waitlist */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center bg-gradient-to-br from-plum-900 to-plum-800 rounded-[3rem] p-10 md:p-16 shadow-2xl relative overflow-hidden mb-20" data-id="element-1222" data-mp="23yicej63ojmy">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cta-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-1223" data-mp="3o9wj7f55m4z1" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blush-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" data-id="element-1224" data-mp="1hn76nfuqga2m" />

          <div className="relative z-10" data-id="element-1225" data-mp="1cpyq1jp8ge3h">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 text-sm font-bold uppercase tracking-wider border border-emerald-400/30 mb-6" data-id="element-1226" data-mp="1rdjo2z793n4j">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-id="element-1227" data-mp="20e841zg7fxk2" />
              Now Live on Google Play
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1228" data-mp="1wbl8pokbrui4">
              From survival mode to thriving mode.
            </h2>
            <p className="text-plum-200 text-lg mb-10 max-w-xl mx-auto" data-id="element-1229" data-mp="25db9hl03k5sk">
              Trippin' Through Life is live on Google Play — download it now and
              start turning chaos into structure, one manageable step at a time.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-xl mx-auto" data-id="element-1230" data-mp="b0le0yu2yoj2">
              {/* Google Play — Live */}
              <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 bg-white text-plum-900 rounded-2xl font-bold hover:bg-blush-50 transition-colors shadow-lg shadow-black/20" data-id="element-1231" data-mp="3qalia8ba0dag">
                <svg className="w-6 h-6" viewBox="0 0 512 512" aria-hidden="true" data-id="element-1232" data-mp="32e7fj7pbblp5">
                  <path fill="#00d2ff" d="M48 59.5v393l205-196.5z" data-id="element-1233" data-mp="3cb3p7gqy6dva" />
                  <path fill="#00f076" d="M48 59.5l257 137-58 59z" data-id="element-1234" data-mp="3ccx0588lfk93" />
                  <path fill="#ffce00" d="M48 452.5l199-196 58 59z" data-id="element-1235" data-mp="3haopbuhuss9l" />
                  <path fill="#ff3a44" d="M305 196.5l60 32c30 16 30 39 0 55l-60 32-58-59z" data-id="element-1236" data-mp="rl4o9r8bl526" />
                </svg>
                <span className="text-left leading-tight" data-id="element-1237" data-mp="2h1qpghu9kbx6">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-plum-500" data-id="element-1238" data-mp="2h177j14m5nj5">
                    Get it on
                  </span>
                  Google Play
                </span>
              </a>

              {/* Apple App Store — Coming Soon */}
              <div className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 bg-white/10 text-plum-200 rounded-2xl font-bold border border-white/20 cursor-default select-none" aria-label="Coming soon to the Apple App Store" data-id="element-1239" data-mp="22bst3em6kfww">
                <svg className="w-6 h-6" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" data-id="element-1240" data-mp="1oe4e2neso2zr">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" data-id="element-1241" data-mp="3iq16ail29cvv" />
                </svg>
                <span className="text-left leading-tight" data-id="element-1242" data-mp="2yefos5gp6es1">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-plum-300/80" data-id="element-1243" data-mp="2ycutgdmk6rt4">
                    Coming soon to
                  </span>
                  App Store
                </span>
                <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-[10px] font-bold uppercase tracking-wider" data-id="element-1244" data-mp="2jh6im5mqj6g9">
                  <ClockIcon className="w-3 h-3" data-id="element-1245" data-mp="2epf10klk4b29" />
                  Soon
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Privacy Policy Link Card */}
        <Link to="/apps/trippin-through-life/privacy" className="group block bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-blush-200 hover:shadow-lg hover:border-blush-300 transition-all" data-id="element-1246" data-mp="1mxfy1ea00804">
          <div className="flex items-center gap-5" data-id="element-1247" data-mp="12xo321h77ho1">
            <div className="w-14 h-14 rounded-2xl bg-blush-100 flex items-center justify-center shrink-0 group-hover:bg-blush-200 transition-colors" data-id="element-1248" data-mp="12yz804mdqvgq">
              <ShieldCheckIcon className="w-7 h-7 text-cta-500" data-id="element-1249" data-mp="1vmcxjhnr0oqt" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1250" data-mp="20l6a97v7obpx">
              <h3 className="font-heading text-xl font-bold text-plum-800 mb-1" data-id="element-1251" data-mp="20k4yt2aa9n7o">
                Privacy Policy
              </h3>
              <p className="text-plum-500 text-sm font-medium" data-id="element-1252" data-mp="1lnnkc26srsgw">
                How we handle your data — effective May 12, 2026
              </p>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-plum-400 group-hover:text-cta-500 group-hover:translate-x-1 transition-all shrink-0" data-id="element-1253" data-mp="a7p7dn8i0mae" />
          </div>
        </Link>
      </div>
    </div>;
}